import type React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Input, Label, Spacer } from '@zillow/constellation';

import type { HTMLElementEvent } from '../../../../models/Types';
import FixedOrVariableSelector from './FixedOrVariableSelector';
import HeaderToggle from './HeaderToggle';
import type { Section } from '../../../../models';
import { ELECTRICITY } from '../../../../constants';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';

const INITIAL_STATE = {
  [ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
  [ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [ELECTRICITY.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
  [ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER]: '',
};

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function Electricity_v1({ section, setSectionValues }: Props): JSX.Element {
  const option1 = ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY;
  const option2 = ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY;

  function setAdditionalFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    const value = event.currentTarget.value;
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_VALUE]: value,
    });
  }

  function setAdditionalFixedAmountSelected(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_SELECTED]: true,
      [ELECTRICITY.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    const updatedValues = {
      [ELECTRICITY.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED),
    };

    setSectionValues(
      updatedValues[ELECTRICITY.FIELD_NAME.IS_ENABLED] === false
        ? {
            ...INITIAL_STATE,
            [option1]: false,
            [option2]: false,
            ...updatedValues,
          }
        : updatedValues,
    );
  }

  function setAdditionalVariableCostSelected(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_VALUE]: 0,
      [ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [ELECTRICITY.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <Spacer marginTop="sm">
        <FixedOrVariableSelector
          legendText="What is the additional fee for electricity?"
          fixedAmountSelected={section.getFieldValue(ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_SELECTED)}
          fixedAmountValue={section.getFieldValue(ELECTRICITY.FIELD_NAME.FIXED_AMOUNT_VALUE)}
          setFixedAmountSelected={setAdditionalFixedAmountSelected}
          setFixedAmountValue={setAdditionalFixedAmountValue}
          setVariableMonthlySelected={setAdditionalVariableCostSelected}
          variableMonthlyCostSelected={section.getFieldValue(ELECTRICITY.FIELD_NAME.VARIABLE_COST_SELECTED)}
        />
      </Spacer>
    );
  }

  function renderOption1Details(): React.ReactNode {
    const electricity_serviceProvider = section.getFieldValue(ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER);

    return (
      <FormField
        marginTop="sm"
        label={<Label>What service provider?</Label>}
        control={
          <Input
            placeholder="(example: Sunrise Electric Company)"
            value={electricity_serviceProvider}
            onChange={setServiceProvider}
          />
        }
      />
    );
  }

  const electricity_isEnabled = section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED);
  const option1Selected = section.getFieldValue(option1);
  const option2Selected = section.getFieldValue(option2);

  return (
    <>
      <HeaderToggle
        header="Electricity"
        subheader="Tenant pays in addition to base rent"
        switchOn={electricity_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {electricity_isEnabled && (
        <>
          <CenteredHR />
          <FormField
            label={<Label>Who will be responsible for electricity set up?</Label>}
            control={
              <RadioListBox
                section={section}
                setSectionValues={setSectionValues}
                option1FieldName={option1}
                option2FieldName={option2}
                option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
              />
            }
          />
          {option1Selected && renderOption1Details()}
          {option2Selected && renderOption2Details()}
        </>
      )}
    </>
  );
}

Electricity_v1.description =
  'Generic Electricity component with variable monthly and fixed charges when Landlord responsibility is responsible';

export default Electricity_v1;
